import type { CSSProperties, FC, MouseEventHandler, MouseEvent } from 'react';
import { useMemo, useState, useCallback, memo } from 'react';
import classNames from 'classnames';
import { Image } from 'antd-mobile';
import flatMap from 'lodash/flatMap';

import type { ProductInfoComboItem, ProductInfoComboProductType, ProductInfoCustomization } from '@/apis/product/types';
import { getCDNAssets } from '@/utils';

import { ProductCardCountStepper } from '../../Menu/List/Card/NumButton';
import { ComboItem, ComboChoicesGroup, PriceNode, MAX_DISPLAY_COMBO_LENGTH } from './ComboDetail';
import { isDCSingleCombo } from '../func';
import { getExtraCustomizationCacheKey } from '../useEditor';

import combodetailStyles from './styles/combodetail.module.less';
import styles from './styles/dynamiccombo.module.less';

export type CountComboItemProps = {
  className?: string;
  style?: CSSProperties;
  data?: ProductInfoComboProductType;
  disabled?: boolean;
  max?: number;
  onAdd?: MouseEventHandler<HTMLDivElement>;
  onSub?: MouseEventHandler<HTMLDivElement>;
  onImgClick?: (e: MouseEvent<HTMLImageElement, Event>) => void;
};

export const CountComboItem: FC<CountComboItemProps> = memo(function _CountComboItem(props) {
  const { className, style, data, disabled: propsDisabled, max, onAdd, onSub, onImgClick } = props;
  const quantity = data?.quantity || 0;
  const isSelected = quantity > 0;
  const sellout = useMemo(() => data?.sellout === true, [data?.sellout]);
  const disabled = useMemo(() => propsDisabled || sellout, [propsDisabled, sellout]);

  const handleImgClick = useCallback(
    (e: MouseEvent<HTMLImageElement, Event>) => {
      if (isSelected) {
        onImgClick?.(e);
      }
    },
    [isSelected, onImgClick],
  );

  const tagImg = useMemo(
    () => (data?.tags && Array.isArray(data.tags) ? data.tags[0]?.image : undefined),
    [data?.tags],
  );

  return (
    <div
      className={classNames(styles.countComboWrapper, className)}
      style={style}
      data-selected={isSelected}
      data-sellout={sellout}>
      {tagImg && <img className={styles.countComboTagImg} src={tagImg} />}
      {sellout && (
        <img className={styles.countComboSelloutImg} src={'https://img.mcd.cn/gallery/0d48166927fbe473.png'} />
      )}
      <div className={styles.countComboBody}>
        <Image
          className={styles.countComboImg}
          placeholder={null}
          fit="scale-down"
          fallback={<img className={styles.countComboImgFallback} src={getCDNAssets('menu_list_default_img.png')} />}
          src={data?.image}
          onClick={handleImgClick}
        />
        <div className={styles.countComboName} data-selected={isSelected}>
          {data?.name}
        </div>
        <div className={styles.countComboAction}>
          {quantity > 0 ? (
            <ProductCardCountStepper
              isGaryStyle
              numClassName={styles.countComboStepperNum}
              count={quantity}
              maxCount={max}
              addDisabled={disabled}
              onAdd={onAdd}
              onSub={onSub}
            />
          ) : (
            <div className={styles.countComboDiffPrice} data-center={disabled}>
              <PriceNode diffPrice={data?.diffPrice} />
              {!disabled && (
                <div className={styles.countComboDiffPriceAdd} onClick={onAdd}>
                  <img className={styles.countComboDiffPriceAddImg} src={getCDNAssets('home_plus.png')} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export type MultiComboChoicesGroupProps = {
  className?: string;
  style?: CSSProperties;
  displayLength?: number;
  data?: ProductInfoComboItem;
  onAdd?: (combo: ProductInfoComboItem, comboProduct: ProductInfoComboProductType) => void;
  onSub?: (combo: ProductInfoComboItem, comboProduct: ProductInfoComboProductType) => void;
  onSelectedClick?: (combo: ProductInfoComboItem, comboProduct: ProductInfoComboProductType) => void;
};

export const MultiComboChoicesGroup: FC<MultiComboChoicesGroupProps> = memo(function _MultiComboChoicesGroup(props) {
  const { className, style, displayLength = MAX_DISPLAY_COMBO_LENGTH, data, onAdd, onSub, onSelectedClick } = props;

  const [isFold, setFlod] = useState(true);

  const onFlodClick = useCallback(() => {
    setFlod((v) => !v);
  }, []);

  const subNumTitle = useMemo(() => {
    if (data) {
      const { maxQuantity, minQuantity = 0 } = data;
      if (maxQuantity && maxQuantity === minQuantity) {
        return `(${maxQuantity}份)`;
      }
      if (maxQuantity && maxQuantity > minQuantity) {
        return `(${minQuantity}~${maxQuantity}份)`;
      }
    }
    return '';
  }, [data]);
  const displayProducts = useMemo(
    () => (isFold && data ? data.comboProducts?.slice(0, displayLength) : data?.comboProducts),
    [data, displayLength, isFold],
  );
  const showMoreBtn = useMemo(
    () => data?.comboProducts && data.comboProducts.length > displayLength,
    [data?.comboProducts, displayLength],
  );
  const [remaining] = useMemo(() => {
    const _total = data?.comboProducts?.map((c) => c.quantity || 0).reduce((a, b) => a + b, 0) || 0;
    const _remaining = data?.maxQuantity && data.maxQuantity > _total ? data.maxQuantity - _total : 0;
    return [_remaining, _total];
  }, [data?.comboProducts, data?.maxQuantity]);

  if (!data) {
    return null;
  }
  return (
    <div className={className} style={style}>
      <div className={styles.mutiChoicesTitle}>
        {data.name}&nbsp;{subNumTitle}
      </div>
      <div className={styles.mutiChoicesBody} data-show-more={showMoreBtn}>
        {displayProducts?.map((p, pIdx) => {
          return (
            <CountComboItem
              key={`multiComboProduct${String(p.code)}${pIdx}`}
              className={styles.mutiChoicesItem}
              data={p}
              max={remaining + (p?.quantity || 0)}
              disabled={remaining === 0}
              onAdd={() => onAdd?.(data, p)}
              onSub={() => onSub?.(data, p)}
              onImgClick={() => onSelectedClick?.(data, p)}
            />
          );
        })}
      </div>
      {showMoreBtn && (
        <div className={combodetailStyles.moreWrapper}>
          <div className={combodetailStyles.moreButton} onClick={onFlodClick}>
            {isFold ? '更多' : '收起'}
          </div>
        </div>
      )}
    </div>
  );
});

type DCIncludeCombosProps = {
  className?: string;
  style?: CSSProperties;
  items?: ProductInfoComboItem[];
  itemsExtraCustomizationObj?: Record<string, ProductInfoCustomization>;
  onCustom?: (
    choicesType: number,
    combo: ProductInfoComboItem,
    comboProduct: ProductInfoComboProductType,
    comboProductIndex?: number,
  ) => void;
};

const DCIncludeCombos: FC<DCIncludeCombosProps> = memo(function _DCIncludeCombos(props) {
  const { className, style, items, itemsExtraCustomizationObj, onCustom } = props;

  const comboProductsGroup = useMemo(
    () =>
      flatMap(items, (c) =>
        (
          c.comboProducts?.map((p) => (p ? ([c, p] as const) : undefined)).filter(Boolean) as [
            ProductInfoComboItem,
            ProductInfoComboProductType,
          ][]
        ).sort((a, b) => (a[1].hasCustomization === 0 && b[1].hasCustomization === 1 ? 1 : 0)),
      ),
    [items],
  );

  return (
    <div className={className} style={style}>
      <div className={combodetailStyles.itemTitle}>已包含</div>
      <div className={combodetailStyles.itemChoices}>
        {comboProductsGroup?.map(([c, p], index) => {
          if (p.quantity && p.quantity > 1) {
            return Array.from({ length: p.quantity }, (_, idx) => idx).map((idx) => (
              <ComboItem
                key={`combonoitem${String(p.code)}${index}${idx}`}
                className={combodetailStyles.itemChoice}
                choicesType={0}
                itemExtraCustomization={
                  p.code && getExtraCustomizationCacheKey(c, p.code, idx)
                    ? itemsExtraCustomizationObj?.[getExtraCustomizationCacheKey(c, p.code, idx)]
                    : undefined
                }
                item={p}
                onCustom={() => onCustom?.(0, c, p, idx)}
              />
            ));
          }
          return (
            <ComboItem
              key={`combonoitem${String(p.code)}${index}`}
              className={combodetailStyles.itemChoice}
              choicesType={0}
              item={p}
              onCustom={() => onCustom?.(0, c, p)}
            />
          );
        })}
      </div>
    </div>
  );
});

export type DynamicComboProps = {
  className?: string;
  style?: CSSProperties;
  items?: ProductInfoComboItem[];
  itemsExtraCustomizationObj?: Record<string, ProductInfoCustomization>;
  onSingleSelect?: (combo: ProductInfoComboItem, comboProduct: ProductInfoComboProductType) => void;
  onSingleCustom?: (
    choicesType: number,
    combo: ProductInfoComboItem,
    comboProduct: ProductInfoComboProductType,
    comboProductIndex?: number,
  ) => void;
  onMultiAdd?: (combo: ProductInfoComboItem, comboProduct: ProductInfoComboProductType) => void;
  onMultiSub?: (combo: ProductInfoComboItem, comboProduct: ProductInfoComboProductType) => void;
  onMultiSelectedClick?: (combo: ProductInfoComboItem, comboProduct: ProductInfoComboProductType) => void;
};

const DynamicCombo: FC<DynamicComboProps> = (props) => {
  const {
    className,
    style,
    items,
    itemsExtraCustomizationObj,
    onSingleSelect,
    onSingleCustom,
    onMultiAdd,
    onMultiSub,
    onMultiSelectedClick,
  } = props;

  const [includeCombos, otherCombos] = useMemo(() => {
    const _includeCombos: ProductInfoComboItem[] = [];
    const _otherCombos: ProductInfoComboItem[] = [];
    items?.forEach((i) => {
      if (Number(i.isIncludeRound) === 1) {
        _includeCombos.push(i);
      } else {
        _otherCombos.push(i);
      }
    });
    return [_includeCombos, _otherCombos];
  }, [items]);

  return (
    <div className={classNames(className)} style={style}>
      {includeCombos && includeCombos.length > 0 && <DCIncludeCombos items={includeCombos} onCustom={onSingleCustom} />}
      {otherCombos.map((c, index) => {
        const isSingle = isDCSingleCombo(c);
        if (isSingle) {
          return (
            <ComboChoicesGroup
              key={`dcOtherCombos${c.name || ''}${index}`}
              disabledExtraCustomPrice
              data={c}
              itemsExtraCustomizationObj={itemsExtraCustomizationObj}
              onSelect={onSingleSelect}
              onCustom={onSingleCustom}
            />
          );
        }
        return (
          <MultiComboChoicesGroup
            key={`dcOtherCombos${c.name || ''}${index}`}
            data={c}
            onAdd={onMultiAdd}
            onSub={onMultiSub}
            onSelectedClick={onMultiSelectedClick}
          />
        );
      })}
    </div>
  );
};

export default DynamicCombo;
